/* eslint-disable no-extra-parens */
import React, { PureComponent } from 'react';
import debounce from 'lodash.debounce';
import { displayInfiniteScrollAd, refreshAd, renderDisplayAdForSlot } from '../../common/actions/displayAdActions';

import PropTypes from 'prop-types';
import { reduxConnect } from '../../common';
import { withScreenSize } from '../../components/common/utilities/withScreenSize.jsx';
import wrapDisplayName from 'recompose/wrapDisplayName';
import { DisplayAdProvider } from '../../common/providers/displayAdProvider';
import { AdVisibilityObserver } from './adVisibilityObserver';

const bannerSlotId = 'wb_banner';
const mapsPageId = 'maps';

const mapStoreStateToProps = (storeState) => {
    const { appState } = storeState?.appState || {};
    const { pageId, pageName, busDomain, enableAds, leadingEdgeDebounceDelay, defaultAdRefeshIntervalSeconds, mapsPageRefreshTimer } = appState || {};
    const { pageVisibility, displayAd } = storeState;
    const locality = appState?.user?.ActiveLocation?.Locality || {};
    const userIsBot = appState?.user?.IsBot;

    return {
        currentRoute: storeState?.currentRoute,
        pageAdsConfigured: displayAd?.pageAdsConfigured,
        adsConfigured: displayAd?.adsConfigured,
        currentConfiguredPageId: displayAd?.currentConfiguredPageId,
        currentConfiguredLatitude: displayAd?.currentConfiguredLatitude,
        currentConfiguredLongitude: displayAd?.currentConfiguredLongitude,
        adRefreshIntervalSeconds: defaultAdRefeshIntervalSeconds || 300,
        leadingEdgeDebounceDelay: leadingEdgeDebounceDelay || 3,
        mapsPageRefreshTimer: mapsPageRefreshTimer || 60,
        enableAds: !userIsBot && enableAds,
        globalAdConfig: displayAd?.adConfig,
        pageId,
        pageName,
        busDomain,
        latitude: locality?.Latitude,
        longitude: locality?.Longitude,
        pageVisibility: pageVisibility?.visible
    };
};

const mapDispatchToProps = {
    renderDisplayAdForSlot,
    refreshAd,
    displayInfiniteScrollAd
};

export function DisplayAdContainer(DisplayAdComponent) {    
    @withScreenSize
    @reduxConnect(mapStoreStateToProps, mapDispatchToProps)
    class DisplayAdWrapper extends PureComponent {
        static propTypes = {
            slotId: PropTypes.string.isRequired,
            width: PropTypes.number.isRequired,
            height: PropTypes.number.isRequired,
            currentRoute: PropTypes.string,
            refreshAdEnabled: PropTypes.bool,
            pageAdsConfigured: PropTypes.bool,
            adsConfigured: PropTypes.bool,
            renderDisplayAdForSlot: PropTypes.func.isRequired,
            enableAds: PropTypes.bool,
            showOnPhone: PropTypes.bool,
            showOnTablet: PropTypes.bool,
            showOnDesktop: PropTypes.bool,
            isMobile: PropTypes.bool,
            isTablet: PropTypes.bool,
            isDesktop: PropTypes.bool,
            refreshAd: PropTypes.func.isRequired,
            adRefreshIntervalSeconds: PropTypes.number.isRequired,
            leadingEdgeDebounceDelay: PropTypes.number,
            mapsPageRefreshTimer: PropTypes.number,
            isInfiniteScrollAd: PropTypes.bool,
            displayInfiniteScrollAd: PropTypes.func.isRequired,
            infiniteScrollIndex: PropTypes.number,
            currentConfiguredPageId: PropTypes.string,
            currentConfiguredLatitude: PropTypes.number,
            currentConfiguredLongitude: PropTypes.number,
            pageId: PropTypes.string,
            pageName: PropTypes.string,
            busDomain: PropTypes.string,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
            pageVisibility: PropTypes.bool,
            refreshWatchParam: PropTypes.oneOfType([
                PropTypes.number,
                PropTypes.string
            ]),
            ignoreVisibility: PropTypes.bool
        };

        static defaultProps = {
            showOnPhone: true,
            showOnTablet: true,
            showOnDesktop: true,
            refreshAdEnabled: true,
            ignoreVisibility: false
        };

        constructor(props) {
            super(props);
            this.state = {
                adLoadFailed: false,
                adLoaded: false,
                adDisplayed: false
            };
            this.adLoadFailure = this.adLoadFailure.bind(this);
            this.setupRefreshTimer = this.setupRefreshTimer.bind(this);
            this.clearRefreshTimer = this.clearRefreshTimer.bind(this);
            this.unregisterAdEvents = this.unregisterAdEvents.bind(this);
            this.displayAd = this.displayAd.bind(this);
        }

        adLoadFailure() {
            this.setState({ adLoadFailed: true });
            this.adAvailable = false;
        }

        setupRefreshTimer() {
            const { slotId, refreshAd, pageVisibility, adRefreshIntervalSeconds, refreshAdEnabled, pageId, globalAdConfig, mapsPageRefreshTimer, isInfiniteScrollAd, infiniteScrollIndex, enableAds } = this.props;
            const infiniteScrollAdDivId = isInfiniteScrollAd ? `${slotId}${infiniteScrollIndex}` : null;
            if (refreshAdEnabled && enableAds) {
                if (this.isVisible && pageVisibility && slotId !== 'x03') {
                    this.clearRefreshTimer();
                    if (pageId?.toLowerCase() === mapsPageId) {
                        this.layerChange = false; // emphasize this runs only once since component re-renders multiple times on maps page for some reason¸¸¸¸
                        this.refreshTimer = setInterval(() => {
                            new DisplayAdProvider().refreshAd(slotId, pageId, globalAdConfig, isInfiniteScrollAd, infiniteScrollAdDivId).then(() => {
                                this.setState({ adLoadFailed: false });
                            }).catch((err) => {
                                console.log(`Failed to refresh ad with slotId: ${slotId}`, err);
                            });   
                            
                        }, mapsPageRefreshTimer * 1000);
                    } else {
                        this.refreshTimer = setInterval(() => {
                            let refreshFailed = false;
                            this.setState(prevState => {
                                if (this.isVisible) {
                                    new DisplayAdProvider().refreshAd(slotId, pageId, globalAdConfig, isInfiniteScrollAd, infiniteScrollAdDivId).then(() => {
                                        // Do nothing for now
                                    }).catch((err) => {
                                        console.error(`Failed to refresh ad with slotId: ${slotId}`, err);
                                        refreshFailed = true;
                                    });  
                                } else {
                                    this.refreshTimer = null;
                                }
                                return {
                                    ...prevState,
                                    adLoadFailed: refreshFailed
                                };
                            });
                        }, adRefreshIntervalSeconds * 1000);
                    }
                }
            }
        }

        clearRefreshTimer() {
            if (this.refreshTimer) {
                clearInterval(this.refreshTimer);

                this.refreshTimer = null;
            }
            if (this.loadEmailWidgetAfterTime) {
                clearTimeout(this.loadEmailWidgetAfterTime);
                this.loadEmailWidgetAfterTime = null;
            }
        }

        getPageConfigData() {
            const { slotId, pageId, globalAdConfig } = this.props;
            const adConfig = globalAdConfig || { pageAdConfigs: [] };
            const pageAdConfig = (adConfig?.pageAdConfigs?.find(pageConfig => pageConfig?.pageId?.toLowerCase() === pageId?.toLowerCase()) || { slots: [] });
            const placement = pageAdConfig?.slots?.find(x => x.slotId === slotId);
            const placementId = placement?.tagId || '';

            return {
                pageAdConfig,
                placementId
            };
        }

        registerAdEvents() {
            this._appNexus = window.apntag;
            if (this._appNexus && this._appNexus.onEvent && !this.adEventsRegistered) {
                const { slotId, isInfiniteScrollAd, infiniteScrollIndex, pageId, isMobile, currentConfiguredPageId } = this.props;
                const registeredSlotId = isInfiniteScrollAd ? slotId + infiniteScrollIndex : slotId;
                this._appNexus.onEvent('adAvailable', registeredSlotId, () => {
                    this.adAvailable = true;
                    if (this.isVisible && !this.state.adDisplayed && !this.state.adLoaded && !this.didAddAvailableRequestedAd) {
                        this.adRequestedAvailable(this.isVisible);
                        this.didAddAvailableRequestedAd = true;
                    }
                });
                this._appNexus.onEvent('adRequestFailure', registeredSlotId, this.adLoadFailure);
                this._appNexus.onEvent('adNoBid', registeredSlotId, this.adLoadFailure);
                this._appNexus.onEvent('adError', registeredSlotId, this.adLoadFailure);
                this._appNexus.onEvent('adBadRequest', registeredSlotId, this.adLoadFailure);
                this._appNexus.onEvent('adLoaded', registeredSlotId, () => {
                    this.setState({
                        adLoaded: true,
                        adDisplayed: true
                    });
                });
                if (registeredSlotId === bannerSlotId) {
                    const adDiv = document.getElementById(registeredSlotId);
                    this._appNexus.onEvent('adLoaded', registeredSlotId, () => this.resizeBanner(adDiv));
                }
                if (pageId === mapsPageId && registeredSlotId === 'maps_x13' && !isMobile) {
                    const mapsPageAdWidth = 300;
                    const mapsPageAdHeight = 250;
                    window.pbjs?.onEvent?.('bidWon', (bid) => {
                        if (bid?.width === mapsPageAdWidth && bid?.height === mapsPageAdHeight) {
                            this.invalidAdSize = false;
                        } else {
                            console.error(`Bid size mismatch. Expected 300x250, Got: ${bid?.width}x${bid?.height} `);
                            this.invalidAdSize = true;
                            this.debouncedAdRefresh?.cancel?.();
                            this.resetAd(registeredSlotId);
                            this.debouncedAdRefresh();
                        }
                    });
                }
                this.adEventsRegistered = true;
            }
        }

        unregisterAdEvents() {
            this._appNexus = window.apntag;
            if (this._appNexus && this._appNexus.offEvent && this.adEventsRegistered) {
                const { slotId, isInfiniteScrollAd, infiniteScrollIndex } = this.props;
                const registeredSlotId = isInfiniteScrollAd ? slotId + infiniteScrollIndex : slotId;
                this._appNexus.offEvent('adRequestFailure', registeredSlotId);
                this._appNexus.offEvent('adNoBid', registeredSlotId);
                this._appNexus.offEvent('adError', registeredSlotId);
                this._appNexus.offEvent('adBadRequest', registeredSlotId);
                this._appNexus.offEvent('adLoaded', registeredSlotId);
                this._appNexus.offEvent('adAvailable', registeredSlotId);
                if (registeredSlotId === bannerSlotId) {
                    this._appNexus.offEvent('adLoaded', registeredSlotId);
                }
            }
        }

        displayAd(slotId) {
            if (!this.state.adLoadFailed && this.props.enableAds && !this.invalidAdSize) {
                this.registerAdEvents();
                if (this.props.isInfiniteScrollAd) {
                    const { globalAdConfig } = this.props;
                    if (!this.pageAdConfig) {
                        this.setState({ adLoadFailure: true });
                        return;
                    }
                    const registeredSlotId = slotId + this.props.infiniteScrollIndex;
                    const adConfig = this.pageAdConfig?.slots?.find(slot => slot.slotId === slotId);
                    if (!adConfig) {
                        this.setState({ adLoadFailure: true });
                        return;
                    }
                    this.props.displayInfiniteScrollAd(registeredSlotId, adConfig, globalAdConfig);
                    this.infiniteScrollAdDisplayed = true;
                } else {
                    this.props.renderDisplayAdForSlot(slotId);
                }
                this.setupRefreshTimer();
            }
        }

        resizeBanner(adDiv) {
            const newWidth = adDiv.contentWindow.document.body.scrollWidth;
            const newHeight = adDiv.contentWindow.document.body.scrollHeight;

            adDiv.style.height = newHeight + 'px';
            adDiv.style.width = newWidth + 'px';
        }

        onChange = (isVisible) => {
            const prevVisibilityState = this.isVisible;
            this.isVisible = isVisible;
            if (isVisible && !this.adAvailable) {
                this.adEventsRegistered = false;
                this.registerAdEvents();
            } else if (!isVisible && this.refreshTimer && this.state.adDisplayed) {
                this.clearRefreshTimer();
            } else if (isVisible && prevVisibilityState === false && this.state.adDisplayed) {
                this.displayAd(this.props.slotId);
            } else {
                this.adRequestedAvailable(isVisible);
            }
        }

        adRequestedAvailable(isVisible) {
            if (this.props.slotId
                && isVisible
                && this.adAvailable
                && !this.state.adDisplayed
                && !this.infiniteScrollAdDisplayed
                && this.props.pageAdsConfigured
                && this.props.adsConfigured
                && this.props.pageId === this.props.currentConfiguredPageId
                && this.props.latitude === this.props.currentConfiguredLatitude
                && this.props.longitude === this.props.currentConfiguredLongitude)
            {
                this.displayAd(this.props.slotId);
            }
        }

        componentDidUpdate(prevProps, prevState) {
            const { adLoaded, adLoadFailed } = this.state;
            const {
                slotId,
                pageAdsConfigured,
                adsConfigured,
                pageId,
                currentConfiguredPageId,
                latitude,
                longitude,
                currentConfiguredLatitude,
                currentConfiguredLongitude,
                pageVisibility,
                globalAdConfig,
                refreshWatchParam } = this.props;

            this.refreshAdUnit = prevProps.refreshWatchParam !== refreshWatchParam;

            if (pageId && pageId !== prevProps.pageId) {
                const { placementId, pageAdConfig } = this.getPageConfigData();
                this.placementId = placementId;
                this.pageAdConfig = pageAdConfig;
                this.didAddAvailableRequestedAd = false;
            }

            if (this.props.slotId) {
                this.registerAdEvents();
            }

            if ((slotId
                && this.isVisible
                && adsConfigured
                && pageAdsConfigured
                && !this.props.isInfiniteScrollAd
                && this.adAvailable
                && !this.state.adDisplayed
                && !this.refreshAdUnit
                && pageId === currentConfiguredPageId
                && latitude === currentConfiguredLatitude
                && longitude === currentConfiguredLongitude)
                || (!this.adAvailable 
                    && !this.state.adDisplayed 
                    && this.props.isInfiniteScrollAd 
                    && !this.infiniteScrollAdDisplayed
                    && !this.refreshAdUnit 
                    && pageAdsConfigured
                    && adsConfigured 
                    && this.isVisible
                    && slotId
                    && pageId === currentConfiguredPageId
                    && latitude === currentConfiguredLatitude
                    && longitude === currentConfiguredLongitude)) {
                
                this.displayAd(slotId);
            }

            if (adLoaded || adLoadFailed || this.refreshAdUnit) {
                if ((latitude && longitude && (prevProps.latitude !== latitude || prevProps.longitude !== longitude))
                    || (pageId && prevProps.pageId !== pageId)) { // reset everything previously enabled in adAvailable, adLoaded events
                    
                    // abort debounce operation if it's still pending when user performs major operations that needs a full page ad update
                    // like moving to a new page
                    this.debouncedAdRefresh?.cancel?.();
                    this.resetAd(slotId);
                }

                if(this.refreshAdUnit) {
                    this.debouncedAdRefresh();
                }
            }

            if(pageVisibility === false && this.refreshTimer) {
                this.clearRefreshTimer();
            }else if(this.state.adDisplayed
                    && pageId === currentConfiguredPageId
                    && prevProps.pageVisibility === false
                    && pageVisibility
                    && !this.refreshTimer
                    && this.isVisible
                    && this.adEventsRegistered) {
                this.setupRefreshTimer();
            }

        }

        componentDidMount() {
            const { placementId, pageAdConfig } = this.getPageConfigData();
            this.placementId = placementId;
            this.pageAdConfig = pageAdConfig;

            if (this.props.slotId) {
                this.registerAdEvents();
            }

            if (Number.isFinite(this.props.leadingEdgeDebounceDelay)) {
                this.debouncedAdRefresh = debounce(this.handleAdRefresh, this.props.leadingEdgeDebounceDelay * 1000, {
                    leading: true,
                    trailing: false
                });   
            }
        }

        componentWillUnmount() {
            this.debouncedAdRefresh?.cancel?.();
            this.clearRefreshTimer();

            //We don't need to unregister the ad events right now because we clear AST when switching pages which unregisters the events automatically.
            // this.unregisterAdEvents();
        }

        resetAd(slotId) {
            if (slotId) {
                const slotDiv = document.getElementById(slotId);
                this.adAvailable = false;
                this.infiniteScrollAdDisplayed = false;
                this.setState({
                    adLoaded: false,
                    adLoadFailed: false,
                    adDisplayed: false
                });

                //HACK: when switching pages for elements in the layout like the banner or brand wraps
                //AST doesn't reclaim the contents of the div and will just keep adding ads to the div
                //so we have to manually clear the div contents when switching pages.

                if (slotDiv) {
                    slotDiv.innerHTML = '';
                }   
            }
        }

        handleAdRefresh() {
            const { slotId, pageId, globalAdConfig, isInfiniteScrollAd, infiniteScrollIndex } = this.props;
            const infiniteScrollAdDivId = isInfiniteScrollAd ? `${slotId}${infiniteScrollIndex}` : null;
            this.resetAd(slotId);
            new DisplayAdProvider().refreshAd(slotId, pageId, globalAdConfig, isInfiniteScrollAd, infiniteScrollAdDivId).then(() => {
                this.setupRefreshTimer();
                this.refreshAdUnit = false;
                this.isRefreshingAd = false;
            }).catch((err) => {
                console.log(`Failed to refresh ad with slotId: ${slotId}`, err);
                this.setupRefreshTimer();
                this.refreshAdUnit = false;
                this.isRefreshingAd = false;
            });
        }

        render() {
            const { 
                height, 
                enableAds, 
                showOnPhone, 
                showOnTablet, 
                showOnDesktop, 
                isMobile, 
                isTablet, 
                isDesktop, 
                slotId, 
                ignoreVisibility,
                isInfiniteScrollAd,
                infiniteScrollIndex 
            } = this.props;
            if ((isMobile && !showOnPhone)
                || (isTablet && !showOnTablet)
                || (isDesktop && !showOnDesktop)) {
                return null;
            }
            const registeredSlotId = isInfiniteScrollAd ? slotId + infiniteScrollIndex : slotId;

            return (
                <AdVisibilityObserver onVisibilityChangeCallback={this.onChange} adSlotId={registeredSlotId}>
                    <DisplayAdComponent {...this.props} slotId={registeredSlotId} adEnabled={enableAds} adLoaded={this.state.adLoaded} />
                </AdVisibilityObserver>
            );
        }
    }

    DisplayAdWrapper.displayName = wrapDisplayName(DisplayAdComponent, 'AdContainerHOC');
    return DisplayAdWrapper;
}
